import { useCallback, useContext } from "react";
import { Accept, FileRejection, useDropzone } from "react-dropzone";
import { Button } from "../Button";
import { toast } from "react-toastify";
import { AccessibilityContext } from "views/AccessibilityContext";

interface Props {
    handleDrop: (files: File[]) => void;
    handleDelete?: (file: File) => void;
    files: File[];
    accept?: Accept;
    vertical?: boolean;
    isMultiple?: boolean;
    errorMessage?: string;
    clearOnDrop?: boolean;
}

export const Dropzone: React.FC<Props> = ({
    handleDrop,
    handleDelete,
    files = [],
    accept,
    errorMessage,
    vertical,
    isMultiple,
    clearOnDrop,
}) => {
    const deleteFile = useCallback(
        (file: File) => handleDelete && handleDelete(file),
        [handleDelete]
    );

    const getAcceptedExtensions = useCallback((): string => {
        if (!accept) return "";
        return Object.values(accept).flat().join(", ");
    }, [accept]);

    const onDrop = useCallback(
        (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            if (rejectedFiles.length > 0) {
                rejectedFiles.forEach(({ file, errors }) => {
                    errors.forEach(error => {
                        switch (error.code) {
                            case "file-invalid-type":
                                toast.error(
                                    `Nepodržan tip datoteke. Podržani tipovi su: ${getAcceptedExtensions()}`
                                );
                                break;
                            case "file-too-large":
                                toast.error("Datoteka je prevelika. Maksimalna veličina je 20MB");
                                break;
                            case "too-many-files":
                                toast.error("Previše datoteka");
                                break;
                            default:
                                toast.error("Greška pri učitavanju datoteke");
                        }
                    });
                });
                return;
            }

            const duplicateFiles = acceptedFiles.filter(newFile =>
                files.some(existingFile => existingFile.name === newFile.name)
            );

            if (duplicateFiles.length > 0) {
                toast.error("Nije moguće učitati više datoteka istog imena");
                return;
            }

            if (acceptedFiles.length + files.length > 3) {
                toast.error("Nije moguće učitati više od 3 datoteke");
                return;
            }

            handleDrop(acceptedFiles);
        },
        [files, handleDrop, getAcceptedExtensions]
    );

    const { getRootProps, getInputProps } = useDropzone({
        accept: accept,
        multiple: isMultiple,
        onDrop,
        maxFiles: isMultiple ? 3 : 1,
        maxSize: 20000000,
    });

    const { lowVision } = useContext(AccessibilityContext);

    return (
        <div className="dropzone-container">
            {files.length !== 0 &&
                !clearOnDrop &&
                files.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="dropzone-file">
                        <span>{file.name}</span>
                        <Button
                            size="small"
                            onClick={() => deleteFile(file)}
                            variant="primary-ghost"
                        >
                            Obriši
                        </Button>
                    </div>
                ))}
            {(isMultiple || files.length === 0) && (
                <div className={`${vertical ? "f-c" : "f-r"} even g-24`}>
                    <div className="dropzone-zone">
                        <div {...getRootProps()}>
                            <input {...getInputProps()} />
                            <div className="dropzone-text">
                                <span>Povuci i ispusti ovdje datoteku</span>
                                <span> ili</span>
                                <Button
                                    size="small"
                                    variant="primary"
                                    lowVision={lowVision}
                                >
                                    Odaberi datoteku
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="f-c">
                        {accept && (
                            <span>
                                Tipovi dokumenata:{" "}
                                {Object.keys(accept)
                                    .map(ft => accept[ft])
                                    .join(", ")}
                            </span>
                        )}
                        <span>Maksimalna veličina: 20MB</span>
                    </div>
                </div>
            )}
            {errorMessage && <p className="error-message">{errorMessage}</p>}
        </div>
    );
};
